<template>
  <div :class="isTrendingEnabled ? 'trending-enabled' : ''">
    <div
      v-if="showInitialLoadingSkeleton"
      id="SkeletonRenderVue"
      v-html="skeleton"
    />
    <div v-if="isCountZero">
      <div class="searched-term-detail">
        <p v-html="resultWithCount" />
        <div class="popular-linklist" />
        <div
          v-if="!isNoResultPage"
          class="mobile-result-counter"
        >
          <p>
            {{ totalItemsToShowQty }} Results for <span v-html="searchTerm" />
          </p>
        </div>
      </div>
      <toolbar
        @column-count="onClickColumnCount"
        @toggle-filters="onToggleFilterPanel"
        @toggle-side-bar-filters-desktop="onToggleFilterPanelDesktop"
      />
      <div
        class="products__wrapper"
        :class="{ 'filters--hidden': toggleFilterSideBar }"
        @click="checkFilterSidebar"
      >
        <div
          v-if="filters.length > 0"
          id="filtersId"
          class="filters"
          :class="{ 'is--open': toggleFilters }"
        >
          <filterSidebar
            v-show="!productItemsSkeletonFlag"
            :filter-data="collection"
            :initial-params="initialParams"
            :total-products="totalItemsToShowQty"
            @filters="onFilterUpdate"
            @order_by="onSortByEvent"
            @close-filters-panel="onToggleFilterPanel"
            @view-results="onToggleFilterPanel"
            @selected-filters="onSelectedFilters"
          />
        </div>
        <div
          v-if="!productItemsSkeletonFlag"
          class="productGrid"
        >
          <div
            v-if="gridContent && gridContent.length > 0 && !isNoResultPage"
            id="collection"
            ref="productCardGrid"
            class="products show--wishlist products-grid"
            :data-column-grid="columnGrid"
          >
            <template
              v-for="(item, index) in gridContent"
              :key="getItemKey(item, index)"
            >
              <product-card
                v-if="item.type === 'product-card'"
                context="collection"
                :grid-active-variant-selector="states.activeVariantSelector"
                :initial-product="item.data"
                :trending-enabled="isTrendingEnabled"
                :is-plp-product="true"
                :lazy-images="index >= 4"
                @link-clicked="handleCardClick($event)"
                @variant-selector-opened="handleVariantSelectorToggle"
              />

              <content-pod
                v-if="item.type === 'content-pod'"
                :content="item.data"
                @link-clicked="handleCardClick($event)"
              />
            </template>

            <skeletonCard
              v-for="(item, index) in columnGrid"
              v-show="!isLoaded"
              :key="index"
            />
          </div>

          <div
            ref="intersection"
            class="products-grid__intersection"
          />

          <div
            v-if="!isSearchTemplate && isNoResultPage"
            class="collection__no-products"
          >
            <span>There are no products that match your search.</span>
          </div>

          <template v-if="totalItemsToShowQty > 0">
            <div
              v-if="!isNoResultPage"
              class="toolbar--bottom"
            />
          </template>
        </div>
      </div>
    </div>
    <noResult v-if="isSearchTemplate && showEmptySection && !isCountZero" />
  </div>
</template>

<script>
import { mapState } from 'vuex'

/**
 * Pinia state.
 * - Added but commented out, for future use.
 * - Product Cards leverage these directly.
 */
import { mapState as mapPiniaState } from 'pinia'

// import { useCartStore } from '~stores/cart'
// import { useFeatureFlagStore } from '~stores/feature-flags'
// import { useLocaleStore } from '~stores/locale'
import { useScreenStore } from '~stores/screen'
// import { useWishlistStore } from '~stores/wishlist'
import { getAttraqtLocaleAttributes, getCurrentStore } from '~/helpers/utils'
import {
  isVisible,
  setIntersectionObserver,
  waitForCondition,
} from '~/helpers/dom'
import { values as breakpoints } from '~/helpers/breakpoints'
import { storageNamespace } from '~/helpers/storage-namespaces'

import SectionMethods from '~/core/mint/methods/section-methods'
import SectionMounted from '~/core/mint/methods/section-mounted'

import ContentPod from '~components/content-pod/content-pod'
import ProductCard from '~components/product-card/product-card'

import toolbar from '~legacy-vue/vueComponents/CollectionTemplate/toolbarManager.vue'
import filterSidebar from '~legacy-vue/vueComponents/CollectionTemplate/filtersContainer.vue'
import skeletonComponent from '~legacy-vue/vueComponents/CollectionTemplate/skeletonComponent'
import skeletonCard from '~legacy-vue/vueComponents/CollectionTemplate/skeletonCard'
import productsItemsSkeleton from '~legacy-vue/vueComponents/CollectionTemplate/skeletonProductCards'
import noResult from '~legacy-vue/vueComponents/CollectionTemplate/noResult.vue'

const collectionPageContainer = {
  name: 'MintVelvetCollectionRenderer',
  delimiters: ['{$', '$}'],
  components: {
    ContentPod,
    toolbar,
    filterSidebar,
    noResult,
    skeletonCard,
    ProductCard,
  },
  data() {
    return {
      columnGrid: 4,
      contentPods: mint.collection?.contentPods,
      currentPage: 1,
      emptySearchMessage: '',
      filteringQueryString: '',
      filters: {},
      filtersApplied: false,
      initialParams: [],
      intersectionRootMargin: '0px',
      isCountZero: false,
      isLoaded: false,
      isNoResultPage: false,
      isSearchTemplate: false,
      isTrendingEnabled: false,
      loadedPages: [],
      mv_source: '',
      noResultMessage: '',
      pagination: {
        currentPage: 1,
        currentProducts: 0,
        perPage: mint.collection.itemsPerPage,
        totalPages: 0,
        totalProducts: 0,
      },
      restoreScroll: {
        currentPage: 0,
        currentProductCount: 0,
        scrollTop: 0,
        state: 'disabled',
      },
      states: {
        activeVariantSelector: false,
      },
      productsItemsSkeleton: productsItemsSkeleton,
      resultWithCount: '',
      searchTerm: '',
      showEmptySection: false,
      showingNoResult: '',
      skeleton: skeletonComponent,
      _sortBy: '',
      startIndex: 0,
      termToFilter: '',
      toggleFilters: false,
      toggleFilterSideBar: false,
      totalItemsToShowQty: 0,
    }
  },

  computed: {

    /**
     * Pinia state.
     * - Added but commented out, for future use.
     * - Product Cards leverage these directly.
     * - Importing *all* of these alongside VueX causes slowdowns, especially
     *   in Development/Vue Dev Tools - be considerate with what's being bought in
     *   and this can be leveraged more once VueX dependency is dissolved.
     */
    // ...mapPiniaState(useCartStore, ['cartData']),
    // ...mapPiniaState(useFeatureFlagStore, ['featureFlags']),
    // ...mapPiniaState(useLocaleStore, ['locale']),
    // ...mapPiniaState(useScreenStore, ['screen']),
    // ...mapPiniaState(useWishlistStore, ['wishlistData']),

    /**
     * Map global state.
     */
    ...mapState([
      'collection',
      'products',
      'productItemsSkeletonFlag',
      'redirect',
      'sortedBy',
      'totalItems',
    ]),

    ...mapPiniaState(useScreenStore, {
      isMobile: 'mobile',
    }),

    /**
     * Calculate if to display the initial loading skeleton.
     * - Displays on Page Load
     * - Should only display
     * @returns {Boolean}
     */
    showInitialLoadingSkeleton() {
      return Boolean(
        !this.isLoaded &&
          Number(this.currentPage) === Number(1) &&
          this.productItemsSkeletonFlag,
      )
    },

    /**
     * Generate an array of content from loaded products and content pods.
     * @returns {Array}
     */
    gridContent() {
      if (!this.products || !this.products.length) {
        return []
      }

      /**
       * Start filling the content array with data from loaded products.
       */
      let content = this.products.map(product => {
        return {
          data: { ...product },
          type: 'product-card',
        }
      })

      if (this.enableContentPods) {
        content = this.addContentPods(content, this.contentPods)
      }

      return content
    },

    /**
     * Calculate whether content pods should be enabled for the current page.
     * - Check if any content pods exist.
     * - Check if the feature flag is enabled.
     * - Check if filters are applied or if the current page is a search page.
     * @returns {Boolean}
     */
    enableContentPods() {
      return (
        this.contentPods &&
        this.contentPods.length &&
        mint.flags.collections.enableContentPods &&
        !this.filtersApplied &&
        !this.isSearchTemplate
      )
    },
  },

  methods: {

    /**
     * Imported from `section-methods.js`.
     * - These methods already run in each /vue/section/* file automatically
     * - Add here to replicate behaviour and sync Pinia
     * - Long term plan is to refactor and move this file to /vue/sections/
     */
    ...SectionMethods(),

    checkFilterSidebar(_event) {
      const target = _event.target
      if (this.toggleFilters) {
        if (target.classList.contains('is--open')) {
          this.toggleFilters = false
        }
      }
    },

    /**
     * Sets the `rootMargin` for the lazy-loading IntersectionObserver.
     * - Gets the height of a given product card and sets that as the rootMargin
     *   of the IntersectionObserver.
     * - Runs within `loadPage`, which runs on load, filter,
     *   sortBy, and grid column switch to always get true height.
     */
    setIntersectionMargin() {
      const productGrid = this.$refs.productCardGrid

      if (!productGrid) {
        return
      }

      /**
       * Note: `querySelectorAll` instead of this.$refs is an anti-pattern.
       * Converting this page to leverage refs is out of scope for the ticket
       * at the time of this change.
       *
       * - Set the margin as height + 25% for safety.
       */
      const productCard = productGrid.querySelectorAll('.product')[0]

      this.intersectionRootMargin = `${productCard.offsetHeight}px` || '100px'
    },

    /**
     * Load the requested page.
     * @param {Number} pageIndex - Index of the page to be loaded. This should
     *   start from one, it will be zero-indexed for querying.
     */
    loadPage(pageIndex) {
      this.isLoaded = false

      /**
       * Early return if the requested page has been loaded or is out of bounds.
       */
      const pageLoaded = this.loadedPages.includes(pageIndex)

      const pageOutOfBounds = this.totalPages && pageIndex > this.totalPages

      if (pageIndex < 1 || pageLoaded || pageOutOfBounds) {
        this.isLoaded = true
        return
      }

      /**
       * Zero-index the target page value for querying, and generate starting
       * product index using the number of items per page.
       */
      const targetPage = pageIndex - 1
      const productIndex = targetPage * this.pagination.perPage

      /**
       * Generate Attraqt query.
       */
      const data = this.getAttraqtPageQuery(productIndex)

      /**
       * Dispatch event and handle response.
       */
      this.$store.dispatch('fetchPage', data).then(res => {
        mint.EventBus.dispatch('attraqt:sourceIdReceived', {
          sourceId: res.rid,
        })

        /**
         * If a 'Redirect' is in-place (Attraqt Admin > Redirects)
         * - A JSON of the Shopify page/collection (/pages/page.json)
         * - If a single product, `res.info.view === 'detail'` and user is
         *  redirected to the first/only product.
         * - Note when VueX migrates to Pinia, this could happen much earlier
         *   on to speed up the redirection journey.
         */
        if (this.redirect.active) {
          window.location.href = this.redirect.url

          /**
           * Regular loading.
           */
        } else {
          const themes = res.universes.universe[0].themes[0].theme

          if (themes.length && themes[0]['custom-fields']) {
            themes[0]['custom-fields']['custom-field'].forEach(themeItem => {
              if (themeItem.name == 'no_results') {
                if (themeItem.value == 'true') {
                  this.isNoResultPage = true
                }
              }
            })
          }

          if (!this.isNoResultPage) {
            if (this.collection[0]['items-section'].items.item.length > 0) {
              this.$store.commit('setProductItemsSkeletonFlag', false)
              this.totalItemsToShowQty = this.$store.getters.getTotalItems

              this.pagination.totalProducts = this.$store.getters.getTotalItems
              this.pagination.totalPages = Math.ceil(
                this.pagination.totalProducts / this.pagination.perPage,
              )

              this.pagination.currentProducts = this.products.length

              this.filters = this.collection[0].facetmap[0].filter
            }
          }
          if (!this.isNoResultPage) {
            this.isCountZero = true
            if (this.isSearchTemplate) {
              document.getElementById(
                'shopify-section-search-content-blocks',
              ).style.display =
                'none'
            }
          } else {
            this.showEmptySection = true
            if (this.isSearchTemplate) {
              document.getElementById(
                'shopify-section-search-content-blocks',
              ).style.display =
                'block'
            }
          }
        }

        /**
         * Register the loaded page and set loaded state.
         * - If the last query has been pulled from cache, add all previous
         *   page counts to the pagination state in bulk so that the *next* query
         *   will pull from the correct page.
         * - If using a fresh query, just increase the count
         */
        if (this.restoreScroll.state === 'enabled') {
          this.loadedPages = Array.from(
            {
              length: this.restoreScroll.currentPage,
            },
            (arr, index) => index + 1,
          )

          this.currentPage = this.restoreScroll.currentPage
          this.pagination.currentPage = this.restoreScroll.currentPage
        } else {
          this.loadedPages.push(pageIndex)
        }

        /**
         * Execute this *outside* of nextTick to avoid rare bug of
         * nextTick not executing, causing an endless visual loading bug.
         */
        this.restoreScrollPosition()

        this.isLoaded = true
      })
    },

    /**
     * Generate a query string for Attraqt with appropriate parameters.
     * @param {Boolean} productIndex - Index of starting product.
     */
    getAttraqtPageQuery(productIndex = 0) {
      const currentTime = this.formatDate(new Date())

      const env = getCurrentStore() ? 'test1' : 'live1'
      const filterString = this.filteringQueryString
      const term = this.termToFilter

      let viewSize = this.pagination.perPage

      if (this.restoreScroll.state === 'enabled') {
        viewSize = this.restoreScroll.currentProductCount
      }

      /**
       * Set default values for the query.
       */
      const queryParams = new URLSearchParams({
        fh_location: '//mintvelvet/en_GB/',
        date_time: currentTime,
        mv_channel: 'web',
        mv_device: this.getDevice(),
        fh_start_index: productIndex,
        fh_view_size: viewSize,
        fh_row_size: this.columnGrid,
        fh_session_id: this.getSessionId(),
        ...getAttraqtLocaleAttributes(),
      })

      /**
       * The mv_source and fh_sort_by parameters are only set when the relevant
       * values exist.
       */
      if (this.mv_source) {
        queryParams.set('mv_source', this.mv_source)
      }

      if (this._sortBy) {
        queryParams.set('fh_sort_by', this._sortBy)
      }

      /**
       * Set the `fh_location` parameter dependent on whether the query is for
       * a search page or a collection page.
       */
      if (this.isSearchTemplate) {
        queryParams.set(
          'fh_location',
          `//mintvelvet/en_GB${filterString}/$s=${term}`,
        )
      } else {
        queryParams.set(
          'fh_location',
          `//mintvelvet/en_GB/categories<{mintvelvet_${term}}${filterString}`,
        )
      }

      /**
       * Append the query string to the correct environment URL.
       */
      const queryString = `https://query.published.${env}.fas.eu1.fredhopperservices.com/fredhopper/query?${queryParams.toString()}`

      return JSON.stringify({ queryString })
    },

    /**
     * Get the unique key for a product or content pod.
     * @param {Object} item - Product or content pod data object.
     * @returns {String}
     */
    getItemKey(item = {}, index) {
      let key = index

      if (item.type === 'content-pod' && item?.data?.block_id) {
        key = item.data.block_id
      }

      if (item.type === 'product-card' && item?.data?.key) {
        key = item.data.key
      }

      return key
    },

    /**
     * When required, restore the user scroll position.
     * - This runs if the user has pressed 'Back' in their browser.
     * - See `setCollectionScrollRestoreState()` for full breakdown.
     */
    restoreScrollPosition() {
      if (this.restoreScroll.state !== 'enabled') {
        return
      }

      let targetScrollTop = Number(this.restoreScroll.scrollTop)

      waitForCondition(() =>
        Boolean(document.documentElement.scrollHeight > targetScrollTop),
      ).then(response => {
        if (!response) {
          targetScrollTop = 0
        }

        window.scrollTo(0, targetScrollTop)

        this.restoreScroll.currentPage = 0
        this.restoreScroll.currentProductCount = 0
        this.restoreScroll.scrollTop = 0
        this.restoreScroll.state = 'disabled'

        sessionStorage.removeItem(storageNamespace.plp.scrollRestore)
      })
    },

    /**
     * Set columns based on viewport width.
     */
    setColumnCount() {

      /**
       * On mobile, enforce 2 column.
       */
      const defaultSize = this.isMobile ? 2 : 4
      let columnCount =
        Number(localStorage.getItem(storageNamespace.plp.columnCount)) ||
        defaultSize

      if (this.isMobile) {

        /**
         * Reset previous desktop preference if on mobile.
         */
        if (columnCount === 4) {
          columnCount = defaultSize
        }
        this.columnGrid = columnCount

        return
      }

      /**
       * Reset previous mobile preference if on desktop.
       */
      if (columnCount === 1) {
        columnCount = defaultSize
      }

      this.columnGrid = columnCount

      this.onClickColumnCount(`${columnCount}`)
    },

    /**
     * Set column count and save preference in local storage.
     * @param {Number} value - Number of columns.
     */
    onClickColumnCount(value) {
      if (value === '1') {
        this.columnGrid = 1
      } else if (value === '2') {
        this.columnGrid = 2
      } else if (value === '4') {
        this.columnGrid = 4
      }

      localStorage.setItem(storageNamespace.plp.columnCount, value)
    },

    routeData() {
      this.isSearchTemplate = Boolean(mint.request.pageType === 'search')

      if (this.isSearchTemplate) {

        /**
         * Must use .split() instead of URLSearchParams / .get() as filters use '&'.
         * - In future, we can probably refactor and escape FHR URL values
         *   so that we can use URLSearchParams.
         */
        return window.location.search.split('q=')[1] || ''
      }

      this.checkParamsOnCreate()

      return mint.collection.handle
    },

    /**
     * Handle filter updates.
     * @param {Array} val - Filter values.
     * @param {Boolean} initialLoad - If the method is running on initial page load.
     */
    onFilterUpdate(val, initialLoad = false) {
      this.isLoaded = false

      /**
       * Prevent query from running twice on page load.
       */
      if (!initialLoad) {
        this.resetPages()
      }

      this.$store.commit('setProductItemsSkeletonFlag', true)

      if (val.length == 0) {
        this.filteringQueryString = ''

        if (!this.isSearchTemplate) {
          this.initialParams = []
          window.history.pushState(
            {},
            document.title,
            window.location.pathname,
          )
        }

        this.loadPage(1)

        return
      }

      this.filtersApplied = true

      const grouped = val.reduce((acc, curr) => {
        acc[curr.filter_on] = [...(acc[curr.filter_on] || []), curr]
        return acc
      }, {})

      const newArray = Object.keys(grouped).map(key => ({
        filter_on: key,
        value: grouped[key].map(item => item.value),
        minVal: grouped[key].map(item => item.minPrice),
        maxVal: grouped[key].map(item => item.maxPrice),
      }))

      /**
       * Build query string.
       */
      let queryString_ = ''

      const newQueryString = newArray.map(item => {
        if (item.filter_on == 'new_in') {
          queryString_ = `/${item.value[0]}`
        } else if (item.filter_on == window.mint.services.attraqt.priceField) {
          let itemMaxVal = parseInt(item.maxVal)
          const itemMinVal = parseInt(item.minVal)
          itemMinVal > 0 ? parseInt(itemMinVal) - 1 : 0
          itemMaxVal = parseInt(itemMaxVal) + 1
          queryString_ = `/${itemMinVal}<${
            window.mint.services.attraqt.priceField
          }<${itemMaxVal}`
        } else if (item.filter_on == 'categories') {
          let index = 0

          if (index == 0) {
            index = index + 1

            if (this.isSearchTemplate) {
              queryString_ = `/${item.filter_on}<{${item.value.join(',')}}`
            } else {
              item.value.push('mintvelvet_' + this.termToFilter)
              queryString_ = `/${item.filter_on}<{${item.value.join(',')}}`
            }
          }
        } else {
          queryString_ = `/${item.filter_on}>{${item.value.join(';')}}`
        }

        return queryString_
      })

      /**
       * Set filtering data property and reload first page.
       */
      this.filteringQueryString = newQueryString.join('')
      this.loadPage(1)
    },

    /**
     * Toggle filters panel.
     */
    onToggleFilterPanel() {
      this.toggleFilters = !this.toggleFilters

      if ($('.product-images-slider').hasClass('slick-initialized')) {
        setTimeout(function() {
          $('.product-images-slider.slick-slider').slick('setPosition')
        }, 100)
      }
    },

    onToggleFilterPanelDesktop(val) {
      this.toggleFilterSideBar = val
    },

    /**
     * Handle changes to the sorting option.
     * @param {String} order
     */
    onSortByEvent(order) {
      this._sortBy = order

      // Fixes a double posting issue I can't get to the bottom of (OC 28.02.23)
      this.startIndex = 0
      this.$store.commit('setProductItemsSkeletonFlag', true)
      this.isTrendingEnabled = false

      // CHeck to see if a non-sort filter is applied e.g Product Type

      /**
       * Must use this method as URLSearchParams / .get() will classify an '&' as a
       * new param and return invalid values.
       * - Attraqt filters are formatted as filtered=[filter_1&filter_2&filter_3]. Example"
       * - /new-in?filtered=[categories=mintvelvet_fulldiscountapplicable1&t_end_use=day20casual]
       */
      const initialFiltered = window.location.search
        ?.split('?filtered=[')[1]
        ?.split(']')[0]
      const _urlParams =
        (initialFiltered?.length > 0
          ? '?filtered=[' + initialFiltered + ']&'
          : '?') + `fh_sort_by=${this._sortBy}`
      window.history.pushState({}, '', window.location.pathname + _urlParams)

      this.resetPages()
      this.loadPage(1)
    },

    fetchAttributes() {
      this.resultWithCount = $('body')
        .find('[data-results-with-count]')
        .attr('data-results-with-count')
      this.searchTerm =
        '\'' +
        $('body')
          .find('[data-search-term1]')
          .attr('data-search-term1') +
        '\''
    },

    onSelectedFilters(val) {
      if (val.length > 0) {
        this.filtersApplied = true
        this.isTrendingEnabled = false

        this.$store.dispatch('fetchFiltersApplied', true).then(() => {
          this.onFilterUpdate(val)
        })
      } else {
        this.filtersApplied = false
        this.isTrendingEnabled = true

        this.$store.dispatch('fetchFiltersApplied', false).then(() => {
          this.startIndex = 0
          this.onFilterUpdate(val)
        })
      }
    },

    checkParamsOnCreate() {
      const searchParams = window.location.search

      // If searchParams contains the string 'filteredBy=' then split between this and '&fbEnd'
      if (searchParams.includes('?filtered')) {
        const filteredBy = searchParams.split('?filtered=[')[1].split(']')[0]
        this.initialParams = filteredBy.replace('?', '').split('&')
      } else {
        this.initialParams = ['']
      }

      if (searchParams.includes('fh_sort_by')) {
        this._sortBy = searchParams.split('fh_sort_by=')[1]
      }
    },

    /**
     * Return a value for a givem param key
     * - Parse the URL for a specific parameter
     * - If one exists, return it or an empty string
     * @returns {String}
     */
    getQueryParam(param) {
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get(param)
    },

    /**
     * Return a value for the UTM source to pass to Attraqt.
     * - Parse the URL for a specific parameter
     * - If one exists, store it in the browser and return it
     * @returns {String|null}
     */
    getMvSource(param) {
      const paramValue = this.getQueryParam(param)
      const now = Date.now()

      if (paramValue) {
        sessionStorage.setItem(param, paramValue)
        sessionStorage.setItem(`${param}_timeout`, now)
        return paramValue
      }

      const sessionValue = sessionStorage.getItem(param)

      if (sessionValue) {
        const savedTime = sessionStorage.getItem(`${param}_timeout`)
        const notExpired =
          (now - savedTime) / 1000 < mint.collection.mvSourceTimeout

        if (notExpired) {
          return sessionValue
        }

        sessionStorage.removeItem(param)
        sessionStorage.removeItem(`${param}_timeout`)
      }

      return ''
    },

    /**
     * Get device type (mobile, tablet, desktop).
     * @returns {String}
     */
    getDevice() {
      const isMobile = /iPhone|Android/i.test(navigator.userAgent)
      const isTablet = /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(
        navigator.userAgent,
      )

      if (isMobile) {
        return 'mobile'
      } else if (isTablet) {
        return 'tablet'
      } else {
        return 'desktop'
      }
    },

    /**
     * Fetch the customers Session ID from Attraqt.
     * - For fresh users, session IDs
     * @returns {String}
     */
    getSessionId() {
      const id = localStorage.getItem(storageNamespace.attraqtSessionId)

      if (id && mint?.customer.canBeTracked) {
        return id
      }

      return ''
    },

    /**
     * Format the date in a readable format for Attraqt.
     * - Required to be in `ISO 8601` format
     * - Attraqt requires `YYYYMMDDTHHMMSS`
     * - Used for tracking, Result Modification, and A/B test scheduling.
     * @param {Object} date - valid new Date() object.
     * @returns {String}
     */
    formatDate(date) {
      let dateObject = [
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate(),
        'T',
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
      ]

      /**
       * Ensure that each date particle has a leading 0 if needed.
       */
      dateObject = dateObject.map((int, index) => {
        if (typeof int !== 'number' || index === 0) {
          return int
        }

        return int.toString().padStart(2, '0')
      })

      return dateObject.join('')
    },

    redirectToPDP(product) {
      product.attribute.forEach(obj => {
        const valueToSave = obj.value[0].value
        switch (obj.name) {
          case 'handle':
            window.location.href =
              mint.shop.localisedShopDomain + '/products/' + valueToSave
        }
      })
    },

    /**
     * Increment the current page, unless all products are loaded or if the
     * component is already loading.
     */
    paginateProducts() {
      if (
        this.productItemsSkeletonFlag ||
        this.products.length === this.totalItemsToShowQty ||
        !this.isLoaded
      ) {
        return
      }

      this.currentPage++
      this.pagination.currentPage = this.currentPage
    },

    /**
     * Set default page state.
     */
    resetPages() {
      window.scrollTo(0, 0)

      this.currentPage = 1
      this.loadedPages = []
      this.startIndex = 0

      this.pagination.currentPage = 1
      this.pagination.currentProducts = 0
    },

    setTrendingState() {
      //Global flag, Local flag, not search, filters not applied // Fallback because on initial load, filtersApplied isn't populated correctly.
      this.isTrendingEnabled =
        mint.flags?.collections?.enableTrending &&
        mint.request?.pageType !== 'search' &&
        !this.filtersApplied &&
        !window.location?.search.includes('filtered')
    },

    /**
     * Set up the intersection observer for loading new pages.
     * - Uses the element placed at the end of the product grid.
     * - Check if the element is visible when the component is mounted, to load
     *   a new page without requiring the user to scroll up and back down.
     */
    observeProductGrid() {
      const targetElement = this.$refs.intersection

      if (isVisible(targetElement)) {
        this.paginateProducts()
      }

      setIntersectionObserver({
        callback: this.handleGridIntersection,
        rootMargin: this.intersectionRootMargin,
        element: targetElement,
        threshold: 0.1,
      })
    },

    /**
     * Handle event when a last row from the current pack of products is scrolled into view.
     * @param {Array} entries - Array of Intersection Observer entries.
     * @param {Object} observer - Intersection Observer.
     */
    handleGridIntersection(entries, observer) {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          return
        }

        /**
         * All of the pages have loaded.
         */
        if (this.pagination.currentPage === this.pagination.totalPages) {
          observer.disconnect()
          return
        }

        if (!this.isLoaded) {
          return
        }

        this.paginateProducts()
      })
    },

    /**
     * Handle events for when a product card link is clicked.
     * @param {Object} payload - Data passed in from Product Card `link-clicked`.
     * @param {String} payload.handle - Shopify product handle.
     * @param {Object} payload.product - Formatted product object.
     */
    handleCardClick({ handle, product }) {

      /**
       * Attraqt tracking event.
       */
      mint.EventBus.dispatch('attraqt:trackEvent', {
        action: 'click',
        target: {
          product: `${product.id}`,
        },
        metadata: {
          locale: 'en_GB',
        },
      })

      /**
       * Preserve scroll position on 'Back' button press.
       */
      if (!mint.flags.collections.enableRestoreScroll) {
        return
      }

      const plpPageTypes = ['collection', 'search']

      if (!handle || !plpPageTypes.includes(mint.request.pageType)) {
        return
      }

      /**
       * Note: also check if search.
       */
      const data = {
        currentPage: this.pagination.currentPage,
        currentProductCount:
          this.pagination.currentPage * this.pagination.perPage,
        product: handle,
        scrollTop:
          document.documentElement.scrollTop || document.body.scrollTop,
        type: mint.request.pageType,
      }

      const type = mint.request.pageType

      if (type === 'collection') {
        data.query = mint.collection.handle
      }

      if (type === 'search') {
        data.query = location.search
      }

      this.$store.dispatch('cacheScrollPosition', data)
    },

    /**
     * Updates the handle of the current open Variant Selector.
     * - This variable is updated from an event inside the Product Card, running
     *   whenever a variant selector is opened.
     * - The handle is passed down to Product Cards
     * - This ensures only one selector is open at a given time.
     * @param {String} handle
     */
    handleVariantSelectorToggle(handle = false) {
      this.states.activeVariantSelector = handle
    },

    /**
     * Calculate if the users scroll position should be restored.
     * - If the user clicks through to a PDP via a Product Card, then goes 'Back'
     * - Ensure that they're taken to the same product card they interacted with.
     * @returns {Boolean}
     */
    setScrollRestorationState() {
      const cache =
        JSON.parse(
          sessionStorage.getItem(storageNamespace.plp.scrollRestore),
        ) || {}

      if (!Object.keys(cache).length) {
        return
      }

      const { currentPage, currentProductCount, scrollTop } = cache

      this.restoreScroll.currentPage = currentPage
      this.restoreScroll.currentProductCount = currentProductCount
      this.restoreScroll.scrollTop = scrollTop
      this.restoreScroll.state = 'enabled'
    },

    /**
     * Insert the provided content pods into the provided content array.
     * @param {Array} content - Array of product data objects.
     * @param {Array} contentPods - Array of content pod data objects.
     */
    addContentPods(content = [], contentPods = []) {

      /**
       * This variable tracks the number of content pods inserted into the
       * array, so each pod's index can be adjusted to account for the ones that
       * have already been spliced into the array.
       */
      let insertedContentPods = 0

      /**
       * Sort content pods by product index.
       * - This accounts for situations where the content pod blocks are not
       *   ordered by product index in the section.
       * - This is required for the insertedContentPods variable to track the
       *   number of content pods accurately.
       */
      contentPods.sort((left, right) => {
        return left.product_index - right.product_index
      })

      for (const contentPod of contentPods) {
        const podIndex = contentPod.product_index + insertedContentPods

        /**
         * Calculate the target page for the content pod, to allow for an early
         * return if the target page has not been loaded. The `+1` is included
         * because pages in the `loadedPages` array are not zero-indexed.
         */
        const targetPage = Math.floor(podIndex / this.pagination.perPage) + 1

        if (!this.loadedPages.includes(targetPage)) {
          continue
        }

        /**
         * Calculate the index of the first product in the grid, and adjust the
         * pod's index by subtracting that value. This accounts for situations
         * where the lowest loaded page is not the first page of products.
         */
        const startIndex = (this.loadedPages[0] - 1) * this.pagination.perPage
        const adjustedIndex = podIndex - startIndex

        const podData = {
          data: { ...contentPod },
          type: 'content-pod',
        }

        /**
         * Insert the content pod at the adjusted index and increment the
         * total of inserted pods.
         */
        content.splice(adjustedIndex, 0, podData)
        insertedContentPods++
      }

      return content
    },
  },

  watch: {

    /**
     * The user has resized their screen, check the columnCount again.
     */
    isMobile() {
      this.setColumnCount()
    },

    /**
     * When the current page updates, load it.
     */
    currentPage() {
      this.loadPage(this.currentPage)
    },

    /**
     * Watch for when the initial loading skeleton on page
     * load turns into the dynamic components.
     */
    productItemsSkeletonFlag() {
      if (this.productItemsSkeletonFlag) {
        return
      }

      this.$nextTick(() => {

        /**
         * Once Products are in the state, set the lazyloading margin.
         */
        this.setIntersectionMargin()
        this.observeProductGrid()
      })
    },

    isCountZero: {
      handler: function(newValue, oldValue) {
        if (newValue != oldValue && newValue) {
          const importPopularLinklist = $(
            '.outside-vue-popular-linklist',
          ).html()
          setTimeout(function() {
            $('.popular-linklist').append(importPopularLinklist)
          }, 100)
        }
      },
    },
  },

  mounted() {

    /**
     * Imported from `section-methods.js`.
     * - Long term refactor of this entire file would mean following patterns
     *   of other /vue/sections/* components
     * - Note: This *must* run first because child components in this file
     *   are not hooked up to Pinia like other page sections/components.
     */
    SectionMounted(this)

    /**
     * Regular page functionality.
     */
    this.setScrollRestorationState()
    this.setTrendingState()
    this.setColumnCount()
    this.fetchAttributes()

    let defaultTerm = this.routeData()

    const _isSearchTemplate = window.location.pathname.includes('/search')
      ? true
      : false

    if (_isSearchTemplate) {
      if (defaultTerm.includes('%20')) {
        defaultTerm = defaultTerm.replace(/%20/g, '\u0020')
      }
    } else {
      if (defaultTerm.includes('-')) {
        defaultTerm = defaultTerm.replace(/-/g, '')
      }
    }

    this.termToFilter = defaultTerm
    let newVal

    if (!_isSearchTemplate && this.initialParams[0].length > 0) {
      newVal = this.initialParams.map(val => {
        const filter_on = val.split('=')[0]
        if (filter_on === 'price') {
          const priceBounds = decodeURIComponent(val)

          const minPrice = priceBounds.split('=')[1].split('<')[0]
          const maxPrice = priceBounds.split('=')[1].split('<')[2]
          const filterField = priceBounds.split('=')[1].split('<')[1]
          return {
            filter_on: filterField,
            minPrice,
            maxPrice,
          }
        }
        return {
          filter_on: val.split('=')[0],
          value: val.split('=')[1],
          name: val.split('=')[1].toUpperCase(),
        }
      })
    }

    this.mv_source = this.getMvSource('utm_campaign')

    if (this.initialParams.length <= 0 || this.initialParams[0].length <= 0) {
      this.loadPage(1)
    } else {
      this.onFilterUpdate(newVal, true)
    }
  },
}

export default collectionPageContainer
</script>
<style lang="scss" scoped>
#swym-component-container .swym-modal-content .swym-product-name,
#swym-component-container .swym-save-wishlist-modal-dialog .swym-product-name {
  display: block;
}
</style>
